import * as React from 'react';
import {
  TextAction,
  Box,
  Icon,
  StackLayout,
} from '@leagueplatform/genesis-core';
import { Link, useLocation } from 'react-router-dom';

const hoverStyles = {
  backgroundColor: '$inputBackgroundHovered',
  textDecoration: 'none',
};

const currentPageStyles = {
  ...hoverStyles,
  // this after element is used for the right border of the nav item when it is active
  // as "border-right" styles on their own didn't quite match the designs.
  '&::after': {
    backgroundColor: '$interactiveActionPrimary',
    borderRadius: '0px $medium $medium 0px',
    content: '',
    height: '100%',
    position: 'absolute',
    insetInlineEnd: '0',
    width: '3px',
  },
};

export const baseNavStyles = {
  borderRadius: '$medium',
  padding: '$half',
  width: '100%',
  '&:focus': {
    zIndex: 1,
  },
  // Suppress the focus ring for mouse clicks — only show it for
  // keyboard navigation. Otherwise the parent nav item keeps a visible
  // focus outline after clicking, even though the child page is active.
  '&:focus:not(:focus-visible)': {
    outline: 'none',
    boxShadow: 'none',
  },
  '&:hover': hoverStyles,
};

export const navStylesWithActiveIndicator = {
  ...baseNavStyles,
  '&[aria-current=page]': currentPageStyles,
};

const nestedNavItemStyles = {
  ...navStylesWithActiveIndicator,
  paddingInlineStart: '$three',
};

/**
 * Props for top-level navigation items that can contain nested items.
 */
export type NavItemProps = {
  name: string;
  path: string;
  icon?: string;
  /**
   * Controls whether the nested `navItems` list is rendered (expanded).
   * The visual "active page" indicator is NOT driven by this — it's
   * derived from `useLocation()` against `path`.
   */
  isActive?: boolean;
  hasLandingPage?: boolean;
  navItems?: NestedNavItemProps[];
};

export type NestedNavItemProps = Pick<NavItemProps, 'name' | 'path'>;

/**
 * Entity list paths include an environment segment
 * (`.../moduleId/environments/:env/list`). The side nav should stay
 * highlighted on the active entity even when the user switches
 * environments, so we compare against the path prefix up to the
 * `/environments/` segment instead of relying on NavLink's strict
 * URL match.
 */
const stripEnvironmentSegment = (path: string): string => {
  const envIndex = path.indexOf('/environments/');
  return envIndex === -1 ? path : path.slice(0, envIndex);
};

/**
 * Active-state check that tolerates environment switches.
 *
 * - `exact`: match only the exact module path (no descendants). Use this
 *   for parent items that have their own children in the nav — otherwise
 *   the parent stays highlighted while a child is active.
 * - default: match the module path or any path underneath it.
 */
const useIsModulePathActive = (path: string, exact = false): boolean => {
  const { pathname } = useLocation();
  const modulePathPrefix = stripEnvironmentSegment(path);
  if (exact) return pathname === modulePathPrefix;
  return (
    pathname === modulePathPrefix || pathname.startsWith(`${modulePathPrefix}/`)
  );
};

export const NestedNavItem = ({ name, path }: NestedNavItemProps) => {
  const isActive = useIsModulePathActive(path);
  return (
    <Box as="li">
      <TextAction
        as={Link}
        to={path}
        emphasis="subtle"
        size="medium"
        aria-current={isActive ? 'page' : undefined}
        css={nestedNavItemStyles}
      >
        {name}
      </TextAction>
    </Box>
  );
};

export const NavItem = ({
  name,
  navItems = [],
  icon,
  path,
  isActive,
  hasLandingPage,
}: NavItemProps) => {
  const hasItems = navItems.length > 0;
  // Show active indicator when there are no child items (flat nav) or when
  // the item itself has a landing page (so the parent link is a destination).
  const showActiveIndicator = !hasItems || hasLandingPage;
  // Parent items with children should only highlight on the exact landing
  // path; otherwise the parent would stay lit while a child is active.
  const isOnItemPath = useIsModulePathActive(path, hasItems);
  const styles = showActiveIndicator
    ? navStylesWithActiveIndicator
    : baseNavStyles;

  return (
    <StackLayout as="li" horizontalAlignment="stretch" spacing="$quarter">
      <TextAction
        as={Link}
        to={path}
        emphasis="subtle"
        size="large"
        aria-current={showActiveIndicator && isOnItemPath ? 'page' : undefined}
        css={styles}
      >
        <StackLayout orientation="horizontal" spacing="$threeQuarters">
          {icon && <Icon icon={icon} />}
          {name}
        </StackLayout>
      </TextAction>
      {isActive && hasItems && (
        <StackLayout as="ul" horizontalAlignment="stretch" spacing="$quarter">
          {navItems.map((item) => (
            <NestedNavItem name={item.name} path={item.path} key={item.path} />
          ))}
        </StackLayout>
      )}
    </StackLayout>
  );
};
